<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-archive-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      :selected="isOpen"
      @sort="sortBy"
      @open="openWorkers"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-total="{ row }"><b>{{ row.total }}</b></template>

      <template #cell-actions="{ row }">
        <div class="rd-tbl__actions">
          <button type="button" class="ui-btn ui-btn--sm" :aria-label="$t('employees')" @click.stop="openWorkers(row, $event)">
            <v-icon size="16">mdi-account-multiple-outline</v-icon>
            <span class="rd-btn__label">{{ $t("employees") }}</span>
          </button>
        </div>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyReportTabReservedMeals from "@/components/company/reports/companyTabs/companyReportTabReservedMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";
import CompanyDesktopReservedWorkers from "./CompanyDesktopReservedWorkers.vue";

/**
 * Rezervni obroci firme na računaru: obrok × smena; klik na red (ili "Radnici") otvara u panelu radnike koji su
 * obrok naručili. Firme, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje iz starog taba
 * (koji nema štampu ni Excel).
 */
export default {
  name: "CompanyDesktopReserved",
  extends: CompanyReportTabReservedMeals,
  mixins: [hasTableSort, hasPeriodRefetch("fetchReservedMeals")],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("meal"), name: true, sortable: true },
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "end", num: true, zero: true, sortable: true })),
        { key: "total", label: this.$t("total"), align: "end", num: true, sortable: true },
        { key: "actions", label: "", align: "end", headCls: "rd-col-actions" },
      ];
    },
    visibleShifts() {
      return SHIFTS.filter((shift) => shift.id < 4 || this.meals.some((meal) => meal[shift.key] > 0));
    },
    rows() {
      const found = this.search ? this.customSearch(this.meals, [{ value: "name" }], this.search) : this.meals;
      return this.sortRows(
        found
          .filter((meal) => meal.total > 0)
          .map((meal, index) => {
            const row = { key: `${meal.id || meal.name}-${index}`, source: meal, name: this.toLatin(meal.name), total: meal.total };
            SHIFTS.forEach((shift) => {
              row[shift.key] = meal[shift.key] || 0;
            });
            return row;
          })
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + row[shift.key], 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_reserved_meals_data");
    },
    emptyText() {
      return this.search ? "" : this.$t("select_date_and_company");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("r_o"), value: this.totals.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    workersKey(row) {
      return `reserved-${row.source.id || row.name}`;
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === this.workersKey(row));
    },
    /** Radnici koji su naručili obrok, u panelu. */
    openWorkers(row, event) {
      this.$emit("open-panel", {
        opener: event && event.currentTarget,
        key: this.workersKey(row),
        root: true,
        title: row.name,
        subtitle: this.$t("employees_who_ordered"),
        component: CompanyDesktopReservedWorkers,
        props: {
          meal: row.source,
          companyIds: this.$store.state.report.selectedFoodCompanies.map((company) => company.id),
          from: this.dates.from,
          to: this.dates.to,
        },
      });
    },
    async copyTable() {
      const headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [row.name, ...this.visibleShifts.map((shift) => row[shift.key]), row.total]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
